import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import axios from 'axios';

import Global from '../../../../../store/global-selectors';
import { CardType } from '../types';
import type { StatusCountMap } from '../hooks/use-all-status-counts';

type AsyncCardCounts = {
  [CardType.UNSHIPPED_LAST_7_DAYS]: number | null;
  [CardType.SHIPPED_TODAY]: number | null;
  [CardType.YESTERDAY_UNSHIPPED]: number | null;
  [CardType.PENDING_RETURNS]: number | null;
  [CardType.AGING_ORDERS_7_TO_30_DAYS]: number | null;
};

type OperationsCardsDataContextValue = {
  statusCountMap: StatusCountMap | null;
  asyncCounts: AsyncCardCounts;
  isLoadingStatusCounts: boolean;
  refetchAllData: () => Promise<void>;
  fetchAsyncData: (cardTypes: CardType[]) => Promise<void>;
};

const OperationsCardsDataContext = createContext<OperationsCardsDataContextValue | null>(null);

type OperationsCardsDataProviderProps = {
  children: React.ReactNode;
  activeCardTypes: CardType[];
  prefetchAll?: boolean;
};

export const OperationsCardsDataProvider = ({ children, activeCardTypes, prefetchAll = false }: OperationsCardsDataProviderProps) => {
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  const [statusCountMap, setStatusCountMap] = useState<StatusCountMap | null>(null);
  const [isLoadingStatusCounts, setIsLoadingStatusCounts] = useState(true);

  const [asyncCounts, setAsyncCounts] = useState<AsyncCardCounts>({
    [CardType.UNSHIPPED_LAST_7_DAYS]: null,
    [CardType.SHIPPED_TODAY]: null,
    [CardType.YESTERDAY_UNSHIPPED]: null,
    [CardType.PENDING_RETURNS]: null,
    [CardType.AGING_ORDERS_7_TO_30_DAYS]: null,
  });

  const asyncCardTypesNeeded = useMemo(() => {
    const asyncTypes = [
      CardType.UNSHIPPED_LAST_7_DAYS,
      CardType.SHIPPED_TODAY,
      CardType.YESTERDAY_UNSHIPPED,
      CardType.PENDING_RETURNS,
      CardType.AGING_ORDERS_7_TO_30_DAYS,
    ];

    if (prefetchAll) {
      return asyncTypes;
    }

    return asyncTypes.filter((type) => activeCardTypes.includes(type));
  }, [activeCardTypes, prefetchAll]);

  const fetchStatusCounts = useCallback(async () => {
    setIsLoadingStatusCounts(true);
    try {
      const response = await axios.get('/ordergrid/shipmentmode/statuses');
      const aggregated: StatusCountMap = {};
      response.data.statusListByStore.forEach((detail: { derivedStatus: string; count: number }) => {
        const status = detail.derivedStatus;
        aggregated[status] = (aggregated[status] || 0) + detail.count;
      });
      setStatusCountMap(aggregated);
    } catch {
      setStatusCountMap(null);
    } finally {
      setIsLoadingStatusCounts(false);
    }
  }, []);

  const fetchAsyncData = useCallback(
    async (cardTypes: CardType[]) => {
      const now = DateTime.now().setZone(sellerTimeZone);
      const todayStart = now.startOf('day');
      const yesterdayStart = todayStart.minus({ days: 1 });
      const sevenDaysAgo = todayStart.minus({ days: 7 });
      const thirtyDaysAgo = todayStart.minus({ days: 30 });

      const updates: Partial<AsyncCardCounts> = {};

      const promises = cardTypes.map(async (cardType) => {
        try {
          switch (cardType) {
            case CardType.UNSHIPPED_LAST_7_DAYS: {
              const response = await axios.post('/ordergrid/shipmentmode/groupby/counts', {
                filter: {
                  orderGridStatus: 'AwaitingShipment',
                  dateTimeFilters: [{ column: 'OrderDateTime', operator: 'Gte', value: sevenDaysAgo.toISO() }],
                },
                groupBy: [{ orderBy: 'Status' }],
              });
              updates[CardType.UNSHIPPED_LAST_7_DAYS] = response.data[0]?.count ?? 0;
              break;
            }

            case CardType.SHIPPED_TODAY: {
              const response = await axios.post('/shippinggrid/simple', {
                page: { pageNumber: 1, pageSize: 1 },
                includeQueryCount: true,
                filter: {
                  dateTimeFilters: [{ column: 'ShipDateTime', operator: 'Gte', value: todayStart.toISO() }],
                  stringFilters: [],
                  numericFilters: [],
                  collectionFilters: [],
                  booleanFilters: [],
                },
              });
              updates[CardType.SHIPPED_TODAY] = response.data.totalCount ?? 0;
              break;
            }

            case CardType.YESTERDAY_UNSHIPPED: {
              const response = await axios.post('/ordergrid/shipmentmode/groupby/counts', {
                filter: {
                  orderGridStatus: 'AwaitingShipment',
                  dateTimeFilters: [
                    { column: 'OrderDateTime', operator: 'Gte', value: yesterdayStart.toISO() },
                    { column: 'OrderDateTime', operator: 'Lt', value: todayStart.toISO() },
                  ],
                },
                groupBy: [{ orderBy: 'Status' }],
              });
              updates[CardType.YESTERDAY_UNSHIPPED] = response.data[0]?.count ?? 0;
              break;
            }

            case CardType.PENDING_RETURNS: {
              const response = await axios.get('/returngrid', {
                params: { returnStatus: 'outstanding', page: 1, pageSize: 1 },
              });
              updates[CardType.PENDING_RETURNS] = response.data?.totalCount ?? 0;
              break;
            }

            case CardType.AGING_ORDERS_7_TO_30_DAYS: {
              const response = await axios.post('/ordergrid/shipmentmode/groupby/counts', {
                filter: {
                  orderGridStatus: 'AwaitingShipment',
                  dateTimeFilters: [
                    { column: 'OrderDateTime', operator: 'Gte', value: thirtyDaysAgo.toISO() },
                    { column: 'OrderDateTime', operator: 'Lt', value: sevenDaysAgo.plus({ days: 1 }).toISO() },
                  ],
                },
                groupBy: [{ orderBy: 'Status' }],
              });
              updates[CardType.AGING_ORDERS_7_TO_30_DAYS] = response.data[0]?.count ?? 0;
              break;
            }
          }
        } catch {
          updates[cardType as keyof AsyncCardCounts] = null;
        }
      });

      await Promise.all(promises);
      setAsyncCounts((prev) => ({ ...prev, ...updates }));
    },
    [sellerTimeZone],
  );

  const refetchAllData = useCallback(async () => {
    await fetchStatusCounts();
    await fetchAsyncData(asyncCardTypesNeeded);
  }, [fetchStatusCounts, fetchAsyncData, asyncCardTypesNeeded]);

  useEffect(() => {
    void fetchStatusCounts();
  }, [fetchStatusCounts]);

  useEffect(() => {
    if (asyncCardTypesNeeded.length > 0) {
      void fetchAsyncData(asyncCardTypesNeeded);
    }
  }, [asyncCardTypesNeeded, fetchAsyncData]);

  const value = useMemo(
    () => ({
      statusCountMap,
      asyncCounts,
      isLoadingStatusCounts,
      refetchAllData,
      fetchAsyncData,
    }),
    [statusCountMap, asyncCounts, isLoadingStatusCounts, refetchAllData, fetchAsyncData],
  );

  return <OperationsCardsDataContext.Provider value={value}>{children}</OperationsCardsDataContext.Provider>;
};

export const useOperationsCardsData = (): OperationsCardsDataContextValue => {
  const context = useContext(OperationsCardsDataContext);
  if (!context) {
    throw new Error('useOperationsCardsData must be used within OperationsCardsDataProvider');
  }
  return context;
};
